import {
  PointerSensor,
  useSensors,
  useSensor,
  KeyboardSensor,
  DndContext,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { DotsSixVertical, X, TrendUp } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { EmptyState } from "@tokenterminal/core/ui/messaging/emptyState/EmptyState"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import {
  type SetStateAction,
  type Dispatch,
  useState,
  type MutableRefObject,
} from "react"
import { twMerge } from "tailwind-merge"
import { ColumnSettingsDropdown } from "./ColumnSettingsDropdown/ColumnSettingsDropdown"
import { getGroupOptionFromValue } from "./ColumnSettingsDropdown/utils"

function SortableItem({
  item,
  index,
  setDraftSelectedColumns,
  isDisabled,
  className,
}: {
  item: {
    label: string | undefined
    value: string | undefined
    group?: string | undefined
    sparkline: boolean
    groupOptions: {
      label: string
      value: string
    }[]
  }
  index: number
  setDraftSelectedColumns: Dispatch<SetStateAction<PresetColumnConfig[]>>
  isDisabled: boolean
  className?: string
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `${item.value}-${index}`,
    disabled: isDisabled,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div
      key={`${item.value}-${index}`}
      className={twMerge(
        "group/sortable-item",
        "flex flex-row gap-2 items-center py-2 pl-2 pr-1 h-[44px] shrink-0 justify-between bg-bg-surface outline outline-1 outline-neutral-100 dark:outline-neutral-800 cursor-grab",
        isDisabled && "opacity-75 cursor-default",
        isDragging && "opacity-0",
        className
      )}
      ref={setNodeRef}
      style={style}
      {...(!isDisabled ? attributes : {})}
      {...(!isDisabled ? listeners : {})}
    >
      <div className="flex items-center gap-2">
        <Icon
          as={DotsSixVertical}
          className={isDisabled ? "opacity-25" : "opacity-100"}
        />
        <div className="flex flex-row gap-2 items-center">
          <div className={twMerge("text-sm", isDisabled && "opacity-75")}>
            {item.group || item.label}
          </div>
          {item.groupOptions.length > 0 && (
            <div className="text-fg-secondary">
              {getGroupOptionFromValue(item.value, item.groupOptions)?.label}
            </div>
          )}
          {item.sparkline && <TrendUp className="w-3 h-3 text-fg-secondary" />}
        </div>
      </div>
      <div className="flex flex-row gap-px items-center">
        {!isDisabled && (
          <>
            {item.groupOptions.length > 0 && (
              <ColumnSettingsDropdown
                item={item}
                index={index}
                setDraftSelectedColumns={setDraftSelectedColumns}
              />
            )}
            <IconButton
              icon={X}
              variant="ghost"
              size="small"
              label="Remove column"
              className="!w-7 !h-7 text-fg-secondary"
              onPress={() => {
                setDraftSelectedColumns((prev) => {
                  return prev.filter((_, i) => i !== index)
                })
              }}
            />
          </>
        )}
      </div>
    </div>
  )
}

export const SelectedColumnsList = ({
  setDraftSelectedColumns,
  selectedItems,
  draftSelectedColumns,
  defaultSelectedColumns,
}: {
  setDraftSelectedColumns: Dispatch<SetStateAction<PresetColumnConfig[]>>
  selectedItems: {
    label: string | undefined
    value: string
    group: string | undefined
    sparkline: boolean
    groupOptions: {
      label: string
      value: string
    }[]
  }[]
  draftSelectedColumns: PresetColumnConfig[]
  defaultSelectedColumns: MutableRefObject<PresetColumnConfig[]>
}) => {
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string)
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (active.id !== over?.id) {
      setDraftSelectedColumns((items) => {
        const oldIndex = items.findIndex(
          (item, index) => `${item.field}-${index}` === active.id
        )
        const newIndex = items.findIndex(
          (item, index) => `${item.field}-${index}` === over?.id
        )

        return arrayMove(items, oldIndex, newIndex)
      })
    }

    setActiveId(null)
  }

  return (
    <div className="flex-1 flex flex-col border border-solid border-border-default rounded-lg overflow-y-auto">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={selectedItems.map((item, index) => `${item.value}-${index}`)}
          strategy={verticalListSortingStrategy}
        >
          <SortableItem
            key="project"
            item={{
              label: "Project",
              value: "project",
              group: undefined,
              sparkline: false,
              groupOptions: [],
            }}
            setDraftSelectedColumns={setDraftSelectedColumns}
            index={-1}
            isDisabled={true}
          />
          {selectedItems.map((item, index) => (
            <SortableItem
              key={`${item.value}-${index}`}
              item={item}
              setDraftSelectedColumns={setDraftSelectedColumns}
              index={index}
              isDisabled={false}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <div className="transform">
              {(() => {
                const activeIndex = selectedItems.findIndex(
                  (item, index) => `${item.value}-${index}` === activeId
                )

                if (activeIndex !== -1) {
                  const activeItem = selectedItems[activeIndex]
                  return (
                    <SortableItem
                      item={activeItem}
                      setDraftSelectedColumns={setDraftSelectedColumns}
                      index={activeIndex}
                      isDisabled={false}
                      className="rounded-lg"
                    />
                  )
                }

                return null
              })()}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {draftSelectedColumns.length === 0 && (
        <div className="flex flex-col items-center justify-center p-8 text-center text-fg-secondary flex-1">
          <EmptyState
            headingText="No columns selected"
            bodyText="Select columns to add to your screener"
            actions={
              <Button
                onPress={() => {
                  setDraftSelectedColumns(defaultSelectedColumns.current)
                }}
              >
                Reset to default
              </Button>
            }
          />
        </div>
      )}
    </div>
  )
}
